<template>
  <a
    :class="['event-card', { 'event-card--past': isPast && !extraClass, 'event-card--hidden': isHidden }, extraClass]"
    :href="link(event.id)"
  >
    <div class="event-card__sub-info">
      <time class="event-card__date" :datetime="event.dates">
        <span>{{ date }}</span>
      </time>
      <div class="event-card__imgs">
        <img
          v-for="speaker in speakersList"
          :key="speaker.name"
          class="event-card__img"
          :src="imageSrc(event.id, speaker.index)"
          :srcset="imageSrc(event.id, speaker.index, '2x')"
          :alt="speaker.name"
        />
      </div>
    </div>
    <div class="event-card__main-info">
      <h3 class="event-card__title" v-html="event.title"></h3>
      <span class="event-card__type" v-html="event.type"></span>
      <span class="event-card__link">{{ textButton }}</span>
    </div>
  </a>
</template>

<script>
import { format } from 'date-fns';
import { ru } from 'date-fns/locale/index.js';

export default {
  name: 'EventCard',

  props: {
    event: Object,
    index: Number,
    isPast: {
      type: Boolean,
      default: false
    },
  },

  data() {
    return {
      imagePath: '/static/promo-static/landing-2021/src/images/pages/events/',
    };
  },
  computed: {
    extraClass() {
      return this.event.extraClass;
    },
    date() {
      const dateFormat = this.isPast ? 'dd.MM.yy' : 'dd.MM';
      return format(new Date(this.event.dates), dateFormat, { locale: ru });
    },
    textButton() {
      return this.isPast ? 'Смотреть запись' : 'Программа и регистрация';
    },
    speakersList() {
      const speakers = this.event.speakers.map((speaker, index) => ({ ...speaker, index: index + 1 }));
      return speakers.length > 5 ? this.shuffleArray(speakers) : speakers;
    },
    isHidden() {
      return this.index > 12;
    },
  },
  methods: {
    imageSrc(id, index, size = '') {
      return `${this.imagePath}${id}/speakers/${index}-events-avatar${size ? `@2x.png ${size}` : '.png'}`;
    },
    link(id) {
      return `/events/${id}`;
    },
    shuffleArray(array) {
      return array.sort(() => Math.random() - 0.5).slice(0, 5);
    },
  },
};
</script>


<style lang="scss">
@use '~styles/core/_variables.scss' as vars;
@use '~styles/core/_mixins.scss' as mixins;
@use '~styles/components/texts';

@keyframes fadeIn {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

// Event card
.event-card {
  position: relative;
  display: block;
  padding: 3.8rem 0;
  margin-bottom: -1px;
  border-top: 1px solid #e3e3e3;
  border-bottom: 1px solid #e3e3e3;
  opacity: 0;
  animation: fadeIn 0.6s forwards;

  @include mixins.media(md) {
    padding: 4.4rem 20rem 4.4rem 13.2rem;
  }

  @include mixins.media(lg) {
    padding: 5.4rem 23rem 5.4rem 17rem;
  }

  $dateHighlightColors: #fcde78, #76f2a8, #81e5f5, #c3bdff, #ff9f86;

  @for $i from 1 through 5 {
    &:nth-child(5n + #{$i}) {
      .event-card__date {
        span {
          @include texts.text-highlight(nth($dateHighlightColors, $i));
        }
      }
    }
  }

  &--hidden {
    display: none;
  }

  &__sub-info {
    @include mixins.media(sm, max) {
      display: flex;
      justify-content: space-between;
    }
  }

  &__date {
    @include mixins.font(2.2rem, 1.16);

    margin-right: 1.2rem;
    color: vars.$colorBase;
    font-weight: vars.$fontWeightMedium;
    letter-spacing: -0.02em;

    @include mixins.media(sm, max) {
      display: flex;

      br {
        display: none;
      }
    }

    @include mixins.media(md) {
      @include mixins.font(2.6rem, 1.18);

      position: absolute;
      top: 4.4rem;
      left: 0;
      margin: initial;
    }

    @include mixins.media(lg) {
      @include mixins.font(3.4rem, 1.24);

      top: 5.4rem;
    }
  }

  &__imgs {
    display: flex;

    @include mixins.media(md) {
      position: absolute;
      top: 5.4rem;
      right: 0;
    }

    @include mixins.media(lg) {
      top: 6.4rem;
    }
  }

  &__img {
    @include mixins.size(2.8rem);

    border-radius: 50%;

    $imgOffsetSm: 0;
    $imgOffsetMd: 0;
    $zIndex: 1;

    @for $i from 1 through 5 {
      &:nth-child(#{$i}) {
        z-index: #{$zIndex};

        $zIndex: $zIndex + 1;
      }

      &:nth-last-child(n + #{$i}) {
        @include mixins.media(sm, max) {
          transform: translateX(#{$imgOffsetSm});

          $imgOffsetSm: $imgOffsetSm + 8px;
        }

        @include mixins.media(md) {
          transform: translateX(#{$imgOffsetMd});

          $imgOffsetMd: $imgOffsetMd + 14px;
        }
      }
    }

    @include mixins.media(md) {
      @include mixins.size(4.4rem);
    }

    @include mixins.media(lg) {
      @include mixins.size(5.6rem);
    }
  }

  &__title {
    @include mixins.font(2.2rem, 1.16);

    margin-top: 1.2rem;
    color: vars.$colorBase;
    font-weight: vars.$fontWeightMedium;
    letter-spacing: -0.005em;

    @include mixins.media(md) {
      @include mixins.font(2.6rem, 1.24);

      margin-top: initial;
      letter-spacing: -0.02em;
    }

    @include mixins.media(lg) {
      @include mixins.font(3.4rem, 1.24);
    }
  }

  &__type {
    @include mixins.font(1.6rem, 1.55);

    display: block;
    margin-top: 0.4rem;
    color: #000;
    font-weight: vars.$fontWeightBase;
    opacity: 0.6;

    @include mixins.media(md) {
      @include mixins.font(1.8rem, 1.55);
    }

    @include mixins.media(lg) {
      @include mixins.font(2.2rem, 1.55);

      margin-top: 0.8rem;
    }
  }

  &__link {
    @include mixins.font(1.3rem, 1.45);

    display: inline-block;
    padding: 4px 10px;
    margin-top: 1.6rem;
    background: vars.$colorBase;
    border-radius: 3rem;
    color: vars.$bgColorBase;
    font-weight: vars.$fontWeightBase;

    @include mixins.media(md) {
      @include mixins.font(1.6rem, 1.45);

      padding: 6px 14px;
    }

    @include mixins.media(lg) {
      @include mixins.font(1.8rem, 1.45);

      margin-top: 2.4rem;
    }

    @include mixins.hover {
      &:not([disabled]):hover {
        opacity: 0.9;
      }
    }
  }

  &--past {
    border-color: #383838 !important;
    background: vars.$bgColorBaseInverted;

    &:first-child {
      border-top: 1px solid;
    }

    .event-card {
      &__title {
        color: vars.$bgColorBase;
      }

      &__type {
        color: vars.$bgColorBase;
      }

      &__sub-info {
        .event-card__date {
          color: #a5a5a5;

          span {
            @include texts.text-highlight(#3e3e3e);

            @include mixins.media(md) {
              background: initial;
            }
          }
        }
      }

      &__link {
        background: rgba(255, 255, 255, 0.9);
        color: vars.$bgColorBaseInverted;
      }
    }
  }

  &--forum {
    padding: 2rem 1.6rem 3.8rem 2.3rem;
    margin: 0 -1.6rem;
    background-color: #ffc8cf;
    background-image: url('~images/pages/events/index/forum-2023/bg-sm.svg');
    background-repeat: no-repeat;
    background-size: 100% 100%;
    border-radius: 12px;

    @include mixins.media(md) {
      padding: 3.2rem 10rem 4.4rem 13.2rem;
      margin: 0 -2rem;
      background-image: url('~images/pages/events/index/forum-2023/bg-md.svg');
      border-radius: 20px;
    }

    @include mixins.media(lg) {
      padding: 4.4rem 23rem 5.4rem;
      background-image: url('~images/pages/events/index/forum-2023/bg-lg.svg');
    }

    @include mixins.media(1280px) {
      margin: 0 -6rem;
    }

    &:first-child {
      border: unset;
    }

    .event-card__title {
      position: relative;

      &::before {
        @include mixins.size(22.3rem, 6.1rem);

        display: block;
        background-image: url('~images/pages/events/hire-2023/banner/logo.svg');
        background-repeat: no-repeat;
        background-size: contain;
        content: '';

        @include mixins.media(md) {
          @include mixins.size(30rem, 8.3rem);
        }

        @include mixins.media(lg) {
          @include mixins.size(38.7rem, 10.72rem);
        }
      }
    }

    time.event-card__date {
      @include mixins.media(md) {
        top: 5.2rem;
        left: 2rem;
      }

      @include mixins.media(lg) {
        top: 6.8rem;
        left: 6rem;
      }

      span {
        &:first-child {
          @include texts.text-highlight(#fff);
        }
      }
    }

    .event-card__logo {
      @include mixins.size(22.3rem, auto);

      @include mixins.media(sm, max) {
        margin-top: 1.2rem;
      }

      @include mixins.media(md) {
        @include mixins.size(30rem, auto);
      }
      @include mixins.media(lg) {
        @include mixins.size(38.7rem, auto);
      }
    }

    .event-card__type {
      margin-top: 1rem;
      color: #000;
      opacity: 1;

      @include mixins.media(md) {
        margin-top: 1.2rem;
      }

      @include mixins.media(lg) {
        margin-top: 0.6rem;
      }
    }

    .event-card__link {
      @include mixins.media(md) {
        margin-top: 2rem;
      }

      @include mixins.media(lg) {
        margin-top: 2.4rem;
      }
    }

    .event-card__imgs {
      @include mixins.media(md) {
        right: 2rem;
      }
      @include mixins.media(lg) {
        top: 7rem;
        right: 6.6rem;
      }
    }
  }

  &--conference2024 {
    padding: 2rem 1.6rem 3.8rem 2.3rem;
    border: unset;
    margin: 0 -1.6rem;
    background-color: #d5b1fa;
    background-image: url('~images/pages/events/index/conference-2024/bg-xs.svg');
    background-repeat: no-repeat;
    background-size: cover;
    border-radius: 12px;

    @include mixins.media(sm, max) {
      margin-top: 3rem;
    }

    @include mixins.media(md) {
      padding: 3.2rem 10rem 4.4rem 13.2rem;
      margin: 0 -2rem;
      background-image: url('~images/pages/events/index/conference-2024/bg-md.svg');
      border-radius: 20px;
    }

    @include mixins.media(lg) {
      padding: 4.4rem 23rem 5.4rem;
      background-image: url('~images/pages/events/index/conference-2024/bg-lg.svg');
    }

    @include mixins.media(xl) {
      margin: 0 -6rem;
    }

    &:first-child {
      border: unset;
    }

    .event-card__title {
      position: relative;

      &::before {
        @include mixins.size(18.73rem, 6rem);

        display: block;
        background-image: url('~images/pages/events/index/conference-2024/logo.svg');
        background-repeat: no-repeat;
        background-size: contain;
        content: '';

        @include mixins.media(md) {
          @include mixins.size(24.7rem, 8.2rem);
        }

        @include mixins.media(lg) {
          @include mixins.size(34.3rem, 11.4rem);
        }
      }
    }

    time.event-card__date {
      @include mixins.media(md) {
        top: 5.2rem;
        left: 2rem;
      }

      @include mixins.media(lg) {
        top: 6.8rem;
        left: 6rem;
      }

      span {
        &:first-child {
          @include texts.text-highlight(#fff);
        }
      }
    }

    .event-card__logo {
      @include mixins.size(22.3rem, auto);

      @include mixins.media(sm, max) {
        margin-top: 1.2rem;
      }

      @include mixins.media(md) {
        @include mixins.size(30rem, auto);
      }
      @include mixins.media(lg) {
        @include mixins.size(38.7rem, auto);
      }
    }

    .event-card__type {
      margin-top: 1rem;
      color: #000;
      opacity: 1;

      @include mixins.media(sm, max) {
        margin-right: -15px;
      }

      @include mixins.media(md) {
        margin-top: 1.2rem;
      }

      @include mixins.media(lg) {
        margin-top: 0.6rem;
      }
    }

    .event-card__link {
      @include mixins.media(md) {
        margin-top: 2rem;
      }

      @include mixins.media(lg) {
        margin-top: 2.4rem;
      }
    }

    .event-card__imgs {
      @include mixins.media(md) {
        right: 2rem;
      }
      @include mixins.media(lg) {
        top: 7rem;
        right: 6.6rem;
      }
    }
  }
}
</style>
